import { PromotionCardPromotionLike } from 'src/app/components/promotion-card/promotion-card.model';
import {
  getEffectivePromotionRate,
  getRegularRate,
} from '../utils/promotion-rate.util';
import {
  IOperatingHourModel,
  Weekday,
} from 'src/app/models/operating-hour.model';

export interface PromotionDetailPromotion extends PromotionCardPromotionLike {
  id?: number;
  description?: string;
  banner?: string | null;
  start?: string | Date;
  operationType?: 'buy' | 'sell';
  operationLimit?: number;
  promotionPrice?: number;
  appendPriceComparison?: boolean;
  branch?: PromotionBranchLike;
  exchangeRate?: PromotionExchangeRateLike;
}

export interface PromotionBranchLike {
  id?: number;
  branchName?: string;
  name?: string;
  latitude?: number | string;
  longitude?: number | string;
  address?: {
    street?: string;
    sublocality?: string;
    borough?: string;
    locality?: string;
    city?: string;
    state?: string;
    zipCode?: string | number;
    latitude?: number;
    longitude?: number;
  };
  contacts?: Array<{ type?: string; value?: string }>;
  schedules?: Record<
    string,
    { open?: boolean; start?: string; end?: string }
  >;
}

export interface PromotionExchangeRateLike {
  id?: number;
  buyPrice?: number;
  sellPrice?: number;
  currency?: {
    name?: string;
    fullName?: string;
    code?: string;
  };
}

export interface PromotionHeadlineView {
  hasStructuredHeadline: boolean;
  headlineLead: string;
  oldPriceLabel: string;
  newPriceLabel: string;
  fallbackTitle: string;
  currencyLabel: string;
  operationLabel: string;
}

export const PRICE_COMPARISON_SUFFIX_PATTERN =
  / de \$[\d,]+(?:\.\d+)? MXN a \$[\d,]+(?:\.\d+)? MXN$/;

export function stripPriceComparisonSuffix(title: string): string {
  return title.replace(PRICE_COMPARISON_SUFFIX_PATTERN, '').trim();
}

function normalizeHeadlineLead(lead: string): string {
  return lead.replace(/\s+de\s*$/i, '').trim();
}

export interface PromotionChipView {
  branchLabel: string;
  appliesToKey: string;
  availabilityLabel: string;
  validityLabel: string;
  discountLabel: string;
  showAppliesTo: boolean;
  showAvailability: boolean;
  showValidity: boolean;
  showDiscount: boolean;
}

export const PROMOTION_EXPIRY_SOON_THRESHOLD_HOURS = 24;

export interface PromotionExpiryUrgencyView {
  showExpiryBadge: boolean;
  expiryBadgeLabel: string;
  isValidityUrgent: boolean;
}

/**
 * Whole hours remaining until end (ceiling).
 * Any positive remainder under 1 hour returns 1.
 * Returns 0 when already expired; null when end is missing or invalid.
 */
export function getHoursUntilPromotionEnd(
  end?: string | Date | null,
): number | null {
  if (end == null) {
    return null;
  }

  const endDate = end instanceof Date ? end : new Date(end);
  if (Number.isNaN(endDate.getTime())) {
    return null;
  }

  const msRemaining = endDate.getTime() - Date.now();
  if (msRemaining <= 0) {
    return 0;
  }

  const MS_PER_HOUR = 60 * 60 * 1000;
  return Math.max(1, Math.ceil(msRemaining / MS_PER_HOUR));
}

function formatExpiryBadgeLabel(
  hoursUntilEnd: number,
  translate: ScheduleTranslateFn,
): string {
  if (hoursUntilEnd === 1) {
    return translate('LANDING.PROMOTIONS.CARD_EXPIRES_IN_ONE', {
      count: String(hoursUntilEnd),
    });
  }
  return translate('LANDING.PROMOTIONS.CARD_EXPIRES_IN_OTHER', {
    count: String(hoursUntilEnd),
  });
}

/**
 * Prefers API-computed hoursUntilEnd / expiringSoon when present;
 * falls back to client-side 24h threshold for older backends.
 */
export function buildPromotionExpiryUrgency(
  promotion: PromotionDetailPromotion | PromotionCardPromotionLike,
  translate: ScheduleTranslateFn,
  thresholdHours: number = PROMOTION_EXPIRY_SOON_THRESHOLD_HOURS,
): PromotionExpiryUrgencyView {
  const hoursUntilEnd =
    promotion.hoursUntilEnd !== undefined
      ? promotion.hoursUntilEnd
      : getHoursUntilPromotionEnd(promotion.end);

  const isUrgentFromApi =
    promotion.hoursUntilEnd !== undefined ? promotion.expiringSoon : undefined;
  const isUrgentFromClient =
    hoursUntilEnd !== null &&
    hoursUntilEnd > 0 &&
    hoursUntilEnd <= thresholdHours;

  const isValidityUrgent =
    isUrgentFromApi !== undefined ? isUrgentFromApi : isUrgentFromClient;

  const showExpiryBadge =
    isValidityUrgent && hoursUntilEnd !== null && hoursUntilEnd > 0;

  if (!showExpiryBadge || hoursUntilEnd === null) {
    return {
      showExpiryBadge: false,
      expiryBadgeLabel: '',
      isValidityUrgent: false,
    };
  }

  return {
    showExpiryBadge: true,
    expiryBadgeLabel: formatExpiryBadgeLabel(hoursUntilEnd, translate),
    isValidityUrgent: true,
  };
}

export interface ScheduleLineView {
  label: string;
  hours: string;
}

export interface GroupedScheduleLineView {
  dayRun: string[];
  start: string;
  end: string;
}

export type ScheduleTranslateFn = (
  key: string,
  params?: Record<string, string>
) => string;

export function formatGroupedScheduleLineLabel(
  line: GroupedScheduleLineView,
  translate: ScheduleTranslateFn
): string {
  const dayName = (dayKey: string) =>
    translate(`HOME_BRANCHES.SCHEDULE_DAYS.${dayKey}`);
  const run = line.dayRun;

  if (run.length === 1) {
    return translate('HOME_BRANCHES.SCHEDULE_SINGLE_DAY', {
      day: dayName(run[0]),
    });
  }
  if (run.length === 2) {
    return translate('HOME_BRANCHES.SCHEDULE_TWO_DAYS', {
      first: dayName(run[0]),
      second: dayName(run[1]),
    });
  }
  return translate('HOME_BRANCHES.SCHEDULE_RANGE', {
    from: dayName(run[0]),
    to: dayName(run[run.length - 1]),
  });
}

export function formatGroupedScheduleHours(
  line: GroupedScheduleLineView,
  translate: ScheduleTranslateFn
): string {
  return translate('HOME_BRANCHES.SCHEDULE_HOURS', {
    start: line.start,
    end: line.end,
  });
}

function hasSignificantFraction(value: number): boolean {
  return Math.round(value * 100) % 100 !== 0;
}

export function formatMxn(
  value: number | undefined | null,
  localeTag: string = 'es-MX'
): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '';
  }

  const numericValue = Number(value);
  const fractionDigits = hasSignificantFraction(numericValue) ? 2 : 0;
  const amount = new Intl.NumberFormat(localeTag, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numericValue);

  return `$${amount} MXN`;
}

function parsePromotionPriceLabelValue(label: string): number | null {
  const normalized = label
    .trim()
    .replace(/\s*MXN\s*$/i, '')
    .replace(/[^\d.,-]/g, '')
    .replace(/,/g, '');

  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizePromotionPriceLabel(
  label: string,
  localeTag: string
): string {
  const trimmed = label.trim();
  if (!trimmed) {
    return '';
  }

  const parsed = parsePromotionPriceLabelValue(trimmed);
  if (parsed !== null) {
    return formatMxn(parsed, localeTag);
  }

  return /MXN/i.test(trimmed) ? trimmed : `${trimmed} MXN`;
}

function formatPromotionDate(
  value: string | Date | undefined,
  localeTag: string
): string {
  if (!value) {
    return '';
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(localeTag, {
    day: 'numeric',
    month: 'long',
  }).format(date);
}

function getCurrencyLabel(promotion: PromotionDetailPromotion): string {
  const currency = promotion.exchangeRate?.currency;
  return (
    currency?.fullName?.trim() ||
    currency?.name?.trim() ||
    currency?.code?.trim() ||
    ''
  );
}

function getRegularPrice(promotion: PromotionDetailPromotion): number | null {
  const rate = promotion.exchangeRate;
  if (!rate || !promotion.operationType) {
    return null;
  }
  return getRegularRate(
    rate.buyPrice,
    rate.sellPrice,
    promotion.operationType,
  );
}

function getEffectivePrice(promotion: PromotionDetailPromotion): number | null {
  const regularPrice = getRegularPrice(promotion);
  if (
    regularPrice === null ||
    promotion.promotionPrice === undefined ||
    promotion.promotionPrice === null ||
    !promotion.operationType
  ) {
    return null;
  }
  return getEffectivePromotionRate(
    regularPrice,
    Number(promotion.promotionPrice),
    promotion.operationType,
  );
}

/**
 * Casa board labels (same as backoffice):
 * buy  = house sells FX (customer buys)  → Venta
 * sell = house buys FX (customer sells) → Compra
 */
export function getBoardOperationI18nKeys(operationType: 'buy' | 'sell'): {
  operationKey: string;
  appliesToKey: string;
} {
  if (operationType === 'sell') {
    return {
      operationKey: 'LANDING.PROMOTIONS.OPERATION_BUY',
      appliesToKey: 'LANDING.PROMOTIONS.APPLIES_TO_BUY',
    };
  }
  return {
    operationKey: 'LANDING.PROMOTIONS.OPERATION_SELL',
    appliesToKey: 'LANDING.PROMOTIONS.APPLIES_TO_SELL',
  };
}

function getOperationLabel(
  promotion: PromotionDetailPromotion,
  translate: ScheduleTranslateFn,
): string {
  if (!promotion.operationType) {
    return '';
  }
  return translate(getBoardOperationI18nKeys(promotion.operationType).operationKey);
}

export function buildPromotionHeadline(
  promotion: PromotionDetailPromotion,
  translate: ScheduleTranslateFn,
  localeTag: string = 'es-MX'
): PromotionHeadlineView {
  const currencyLabel = getCurrencyLabel(promotion);
  const operationLabel = getOperationLabel(promotion, translate);
  const headlineLead = (
    promotion.headline_lead ??
    promotion.headlineLead ??
    ''
  ).trim();
  const oldPriceLabel = (
    promotion.old_price_label ??
    promotion.oldPriceLabel ??
    ''
  ).trim();
  const newPriceLabel = (
    promotion.new_price_label ??
    promotion.newPriceLabel ??
    ''
  ).trim();

  if (headlineLead && oldPriceLabel && newPriceLabel) {
    return {
      hasStructuredHeadline: true,
      headlineLead: normalizeHeadlineLead(headlineLead),
      oldPriceLabel: normalizePromotionPriceLabel(oldPriceLabel, localeTag),
      newPriceLabel: normalizePromotionPriceLabel(newPriceLabel, localeTag),
      fallbackTitle: promotion.title ?? '',
      currencyLabel,
      operationLabel,
    };
  }

  if (promotion.appendPriceComparison === false) {
    return {
      hasStructuredHeadline: false,
      headlineLead: '',
      oldPriceLabel: '',
      newPriceLabel: '',
      fallbackTitle: promotion.title ?? '',
      currencyLabel,
      operationLabel,
    };
  }

  const regularPrice = getRegularPrice(promotion);
  const effectivePrice = getEffectivePrice(promotion);

  if (
    regularPrice !== null &&
    effectivePrice !== null &&
    !Number.isNaN(regularPrice) &&
    !Number.isNaN(effectivePrice) &&
    currencyLabel
  ) {
    const titleBase = stripPriceComparisonSuffix(promotion.title ?? '');
    const headlineLeadComputed = titleBase
      ? titleBase
      : `${operationLabel} ${currencyLabel}`.trim();
    const oldPrice = regularPrice;
    const newPrice = effectivePrice;
    return {
      hasStructuredHeadline: true,
      headlineLead: headlineLeadComputed,
      oldPriceLabel: formatMxn(oldPrice, localeTag),
      newPriceLabel: formatMxn(newPrice, localeTag),
      fallbackTitle: promotion.title ?? '',
      currencyLabel,
      operationLabel,
    };
  }

  return {
    hasStructuredHeadline: false,
    headlineLead: '',
    oldPriceLabel: '',
    newPriceLabel: '',
    fallbackTitle:
      promotion.short_title ?? promotion.title ?? '',
    currencyLabel,
    operationLabel,
  };
}

export function buildPromotionChips(
  promotion: PromotionDetailPromotion,
  translate: ScheduleTranslateFn,
  localeTag: string = 'es-MX'
): PromotionChipView {
  const branchLabel = promotion.branch?.branchName ?? promotion.branch?.name ?? '';

  const availabilityFromApi = (
    promotion.availability_label ??
    promotion.availabilityLabel ??
    ''
  ).trim();
  const currencyCode = promotion.exchangeRate?.currency?.name ?? '';
  const remainingAmount =
    promotion.availableAmount != null
      ? Number(promotion.availableAmount)
      : promotion.operationLimit != null
        ? Number(promotion.operationLimit)
        : null;
  const availabilityLabel =
    availabilityFromApi ||
    (remainingAmount != null && currencyCode
      ? translate('LANDING.PROMOTIONS.CARD_AVAILABILITY', {
          amount: new Intl.NumberFormat(localeTag).format(remainingAmount),
          currency: currencyCode,
        })
      : '');

  const validityFromApi = (
    promotion.validity_label ??
    promotion.validityLabel ??
    ''
  ).trim();
  const startLabel = formatPromotionDate(promotion.start, localeTag);
  const endLabel = formatPromotionDate(promotion.end, localeTag);
  const validityLabel =
    validityFromApi ||
    (startLabel && endLabel
      ? translate('LANDING.PROMOTIONS.CARD_VALID_FROM_TO', {
          start: startLabel,
          end: endLabel,
        })
      : endLabel
        ? translate('LANDING.PROMOTIONS.CARD_VALID_UNTIL', { date: endLabel })
        : startLabel
          ? translate('LANDING.PROMOTIONS.CARD_VALID_FROM', { start: startLabel })
          : '');

  const discountFromApi = (
    promotion.discount_label ??
    promotion.discountLabel ??
    ''
  ).trim();
  const regularPrice = getRegularPrice(promotion);
  const effectivePrice = getEffectivePrice(promotion);
  let discountLabel = discountFromApi;
  if (
    !discountLabel &&
    regularPrice !== null &&
    effectivePrice !== null &&
    !Number.isNaN(regularPrice) &&
    !Number.isNaN(effectivePrice)
  ) {
    const diff = Math.abs(regularPrice - effectivePrice);
    const currencyCode = promotion.exchangeRate?.currency?.name ?? '';
    if (diff > 0 && currencyCode) {
      discountLabel = translate('LANDING.PROMOTIONS.CARD_DISCOUNT', {
        amount: diff.toFixed(2),
        currency: currencyCode,
      });
    }
  }

  const appliesToKey = promotion.operationType
    ? getBoardOperationI18nKeys(promotion.operationType).appliesToKey
    : 'LANDING.PROMOTIONS.APPLIES_TO_BUY';

  return {
    branchLabel,
    appliesToKey,
    availabilityLabel,
    validityLabel,
    discountLabel,
    showAppliesTo: !!promotion.operationType,
    showAvailability: !!availabilityLabel,
    showValidity: !!validityLabel,
    showDiscount: !!discountLabel,
  };
}

export function getBranchFullAddress(branch: PromotionBranchLike | undefined): string {
  if (!branch?.address) {
    return '';
  }
  const address = branch.address;
  const parts = [
    address.street,
    address.sublocality || address.borough,
    address.locality || address.city,
    address.state,
    address.zipCode,
  ].filter((part) => part !== undefined && part !== null && `${part}`.trim());
  return parts.join(', ');
}

export function getBranchLatitude(branch: PromotionBranchLike | undefined): number {
  const lat = branch?.address?.latitude ?? branch?.latitude;
  return lat !== undefined && lat !== null ? Number(lat) : 0;
}

export function getBranchLongitude(branch: PromotionBranchLike | undefined): number {
  const lng = branch?.address?.longitude ?? branch?.longitude;
  return lng !== undefined && lng !== null ? Number(lng) : 0;
}

export function getBranchPhone(branch: PromotionBranchLike | undefined): string {
  return (
    branch?.contacts?.find((contact) => contact.type === 'PHONE')?.value ?? ''
  );
}

export function getBranchWhatsapp(branch: PromotionBranchLike | undefined): string {
  return (
    branch?.contacts?.find((contact) => contact.type === 'WHATSAPP')?.value ?? ''
  );
}

export function getMapsUrl(branch: PromotionBranchLike | undefined): string {
  const lat = getBranchLatitude(branch);
  const lng = getBranchLongitude(branch);
  return `https://www.google.com/maps/search/?api=1&query=${lat}%2C${lng}`;
}

export function buildScheduleLines(
  branch: PromotionBranchLike | undefined
): ScheduleLineView[] {
  const schedules = branch?.schedules;
  if (!schedules) {
    return [];
  }

  const weekdayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const saturdayKey = 'saturday';

  const weekdayHours = formatGroupedHours(schedules, weekdayKeys);
  const saturdayHours = formatDayHours(schedules[saturdayKey]);

  const lines: ScheduleLineView[] = [];
  if (weekdayHours) {
    lines.push({ label: 'Lunes a Viernes:', hours: weekdayHours });
  }
  if (saturdayHours) {
    lines.push({ label: 'Sábados:', hours: saturdayHours });
  }
  return lines;
}

const WEEKDAY_TO_SCHEDULE_KEY: Record<Weekday, string> = {
  [Weekday.MONDAY]: 'monday',
  [Weekday.TUESDAY]: 'tuesday',
  [Weekday.WEDNESDAY]: 'wednesday',
  [Weekday.THURSDAY]: 'thursday',
  [Weekday.FRIDAY]: 'friday',
  [Weekday.SATURDAY]: 'saturday',
  [Weekday.SUNDAY]: 'sunday',
};

export function buildScheduleLinesFromOperatingHours(
  hours: IOperatingHourModel[]
): ScheduleLineView[] {
  if (!hours?.length) {
    return [];
  }
  const schedules = mapOperatingHoursToSchedules(hours);
  return buildScheduleLines({ schedules });
}

export function buildGroupedScheduleLinesFromOperatingHours(
  hours: IOperatingHourModel[]
): GroupedScheduleLineView[] {
  if (!hours?.length) {
    return [];
  }
  const schedules = mapOperatingHoursToSchedules(hours);
  return buildGroupedScheduleLines(schedules);
}

export function buildGroupedScheduleLines(
  schedules: Record<string, { open?: boolean; start?: string; end?: string }>
): GroupedScheduleLineView[] {
  const slotToDays = new Map<string, string[]>();

  for (const key of SCHEDULE_DAY_ORDER) {
    const day = schedules[key];
    if (!day?.start || !day?.end || day.open === false) {
      continue;
    }
    const slot = `${day.start}|${day.end}`;
    const days = slotToDays.get(slot) ?? [];
    days.push(key);
    slotToDays.set(slot, days);
  }

  const lines: Array<GroupedScheduleLineView & { sortIndex: number }> = [];

  for (const [slot, keys] of slotToDays.entries()) {
    const [start, end] = slot.split('|');
    const runs = splitIntoConsecutiveDayRuns(keys, SCHEDULE_DAY_ORDER);

    for (const run of runs) {
      lines.push({
        dayRun: run,
        start,
        end,
        sortIndex: SCHEDULE_DAY_ORDER.indexOf(run[0]),
      });
    }
  }

  return lines
    .sort((a, b) => a.sortIndex - b.sortIndex)
    .map(({ dayRun, start, end }) => ({ dayRun, start, end }));
}

const SCHEDULE_DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

function mapOperatingHoursToSchedules(
  hours: IOperatingHourModel[]
): Record<string, { open: boolean; start?: string; end?: string }> {
  const schedules: Record<
    string,
    { open: boolean; start?: string; end?: string }
  > = {};

  for (const hour of hours) {
    const key = WEEKDAY_TO_SCHEDULE_KEY[hour.weekday];
    if (!key) {
      continue;
    }
    schedules[key] = {
      open: true,
      start: formatOperatingHourTime(hour.open_time),
      end: formatOperatingHourTime(hour.close_time),
    };
  }

  return schedules;
}

function splitIntoConsecutiveDayRuns(
  keys: string[],
  order: string[]
): string[][] {
  const sorted = [...keys].sort(
    (a, b) => order.indexOf(a) - order.indexOf(b)
  );
  const runs: string[][] = [];
  let current: string[] = [];

  for (const key of sorted) {
    if (!current.length) {
      current = [key];
      continue;
    }

    const lastIndex = order.indexOf(current[current.length - 1]);
    const keyIndex = order.indexOf(key);
    if (keyIndex === lastIndex + 1) {
      current.push(key);
    } else {
      runs.push(current);
      current = [key];
    }
  }

  if (current.length) {
    runs.push(current);
  }

  return runs;
}

function formatOperatingHourTime(time: string): string {
  if (!time) {
    return '';
  }
  const [hourPart, minutePart] = time.split(':');
  const hour = Number.parseInt(hourPart, 10);
  if (Number.isNaN(hour)) {
    return time;
  }
  const minute = minutePart ?? '00';
  return `${hour}:${minute}`;
}

function formatGroupedHours(
  schedules: Record<string, { open?: boolean; start?: string; end?: string }>,
  keys: string[]
): string {
  const openDays = keys.filter((key) => schedules[key]?.open !== false);
  if (!openDays.length) {
    return '';
  }
  const first = schedules[openDays[0]];
  if (!first?.start || !first?.end) {
    return '';
  }
  const allSame = openDays.every(
    (key) =>
      schedules[key]?.start === first.start && schedules[key]?.end === first.end
  );
  if (!allSame) {
    return '';
  }
  return `${first.start} - ${first.end} hrs`;
}

function formatDayHours(day?: {
  open?: boolean;
  start?: string;
  end?: string;
}): string {
  if (!day || day.open === false || !day.start || !day.end) {
    return '';
  }
  return `${day.start} - ${day.end} hrs`;
}
